/**
 * Determines the cart changes required to keep one complimentary bundle
 * alongside an eligible key-fob product.
 *
 * @param {{items?: Array<{key: string, product_id: number, variant_id: number, quantity: number}>}} cart
 * @param {{eligibleProductId: number, giftVariantId: number}} config
 * @returns {{addGift: boolean, updates: Record<string, number>}}
 */
export function planGiftBundleSync(cart, config) {
  const items = cart.items ?? [];
  const hasEligibleProduct = items.some(
    (item) => String(item.product_id) === String(config.eligibleProductId)
  );
  const giftItems = items.filter(
    (item) => String(item.variant_id) === String(config.giftVariantId)
  );

  if (!hasEligibleProduct && giftItems.length > 0) {
    return {
      addGift: false,
      updates: Object.fromEntries(giftItems.map((item) => [item.key, 0])),
    };
  }

  if (hasEligibleProduct && giftItems.length > 0) {
    const updates = {};
    const [primaryGift, ...duplicateGifts] = giftItems;

    if (primaryGift.quantity !== 1) updates[primaryGift.key] = 1;
    for (const gift of duplicateGifts) updates[gift.key] = 0;

    return { addGift: false, updates };
  }

  return { addGift: hasEligibleProduct && giftItems.length === 0, updates: {} };
}

/**
 * Reconciles the complimentary bundle with the current cart.
 *
 * @param {object} params
 * @param {typeof fetch} params.fetcher
 * @param {{eligibleProductId: number, giftVariantId: number, cartUrl: string, cartAddUrl: string, cartUpdateUrl: string}} params.config
 * @param {string[]} params.sectionIds
 * @param {string} params.sectionsUrl
 * @param {(cart: object, sections: Record<string, string>) => void} params.onCartUpdated
 * @returns {Promise<boolean>} Whether the cart was changed.
 */
export async function syncGiftBundle({
  fetcher,
  config,
  sectionIds,
  sectionsUrl,
  onCartUpdated,
}) {
  const cartResponse = await fetcher(config.cartUrl);
  if (!cartResponse.ok) throw new Error('Unable to read the cart');

  const cart = await cartResponse.json();
  const plan = planGiftBundleSync(cart, config);
  const hasUpdates = Object.keys(plan.updates).length > 0;
  if (!plan.addGift && !hasUpdates) return false;

  const requestBody = plan.addGift
    ? {
        items: [
          {
            id: config.giftVariantId,
            quantity: 1,
            properties: {
              _complimentary_bundle: 'true',
              Includes: 'Alcantara keychain, transparent protective case & tool kit',
            },
          },
        ],
        sections: sectionIds.join(','),
        sections_url: sectionsUrl,
      }
    : {
        updates: plan.updates,
        sections: sectionIds.join(','),
        sections_url: sectionsUrl,
      };

  const mutationResponse = await fetcher(plan.addGift ? config.cartAddUrl : config.cartUpdateUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(requestBody),
  });
  if (!mutationResponse.ok) throw new Error('Unable to update the complimentary bundle');

  const mutation = await mutationResponse.json();
  const refreshedResponse = await fetcher(config.cartUrl);
  if (!refreshedResponse.ok) throw new Error('Unable to refresh the cart');

  const refreshedCart = await refreshedResponse.json();
  onCartUpdated(refreshedCart, mutation.sections ?? {});
  return true;
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  class GiftBundleManager extends HTMLElement {
    #syncing = false;
    #syncRequested = false;
    #abortController = new AbortController();

    connectedCallback() {
      document.addEventListener('cart:update', this.#requestSync, {
        signal: this.#abortController.signal,
      });
      this.#requestSync();
    }

    disconnectedCallback() {
      this.#abortController.abort();
    }

    #requestSync = (event) => {
      if (event?.detail?.data?.source === 'gift-bundle-manager') return;

      this.#syncRequested = true;
      if (!this.#syncing) this.#drainSyncQueue();
    };

    async #drainSyncQueue() {
      this.#syncing = true;

      try {
        while (this.#syncRequested) {
          this.#syncRequested = false;
          await this.#syncOnce();
        }
      } catch (error) {
        console.error('Complimentary bundle sync failed:', error);
      } finally {
        this.#syncing = false;
      }
    }

    async #syncOnce() {
      const eligibleProductId = Number(this.dataset.eligibleProductId);
      const giftVariantId = Number(this.dataset.giftVariantId);
      if (!eligibleProductId || !giftVariantId) return;

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);

      await syncGiftBundle({
        fetcher: window.fetch.bind(window),
        config: {
          eligibleProductId,
          giftVariantId,
          cartUrl: `${Theme.routes.cart_url}.js`,
          cartAddUrl: Theme.routes.cart_add_url,
          cartUpdateUrl: Theme.routes.cart_update_url,
        },
        sectionIds: [...new Set(sectionIds)],
        sectionsUrl: window.location.pathname,
        onCartUpdated: (cart, sections) => {
          this.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: this.id,
                data: {
                  source: 'gift-bundle-manager',
                  itemCount: cart.item_count,
                  sections,
                },
              },
            })
          );
        },
      });
    }
  }

  if (!customElements.get('gift-bundle-manager')) {
    customElements.define('gift-bundle-manager', GiftBundleManager);
  }
}
